import { useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { FormInput } from '@/components/FormInput';
import type { HealthPlanRequest, OvertimeRequest, RaroInvestRequest } from '../../../../../shared/dpRequests';
import { describeRequestStatus } from '../../dpRequests.definitions';
import {
    useDecideHealthPlan,
    useDecideOvertime,
    useDecideRaroInvest,
    useMarkRaroInvestReimbursed,
} from '../../hooks/data/useDpRequestMutations';
import { DecisionActions } from '../DecisionActions';
import { HealthPlanSummary, OvertimeSummary, RaroInvestSummary } from '../RequestSummaries';
import { StatusPill } from '../StatusPill';

export function OvertimeReview({ request }: { request: OvertimeRequest }) {
    const decide = useDecideOvertime();
    const meta = describeRequestStatus(request);

    return (
        <div className="space-y-6">
            <StatusPill label={meta.label} tone={meta.tone} />
            <OvertimeSummary request={request} />
            {request.status === 'pending_dp' && (
                <DecisionActions
                    busy={decide.isPending}
                    primaryLabel="Marcar como lançada"
                    helper="Confira o PDF com a aprovação do gestor antes de lançar."
                    onPrimary={() => decide.mutateAsync({ requestId: request.id, action: 'register' })}
                    onReject={note => decide.mutateAsync({ requestId: request.id, action: 'reject', note })}
                />
            )}
        </div>
    );
}

export function RaroInvestReview({ request }: { request: RaroInvestRequest }) {
    const decide = useDecideRaroInvest();
    const reimburse = useMarkRaroInvestReimbursed();
    const meta = describeRequestStatus(request);
    const reimbursed = request.status === 'reimbursed';

    return (
        <div className="space-y-6">
            <StatusPill label={meta.label} tone={meta.tone} />
            <RaroInvestSummary request={request} />

            {request.status === 'pending_dp' && (
                <DecisionActions
                    busy={decide.isPending}
                    primaryLabel="Aceitar"
                    rejectLabel="Recusar"
                    helper="Confira o comprovante. Depois de aceitar, marque quando o reembolso for feito."
                    onPrimary={() => decide.mutateAsync({ requestId: request.id, action: 'approve' })}
                    onReject={note => decide.mutateAsync({ requestId: request.id, action: 'reject', note })}
                />
            )}

            {(request.status === 'approved' || reimbursed) && (
                <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-raro-gray-200 bg-white px-4 py-3 text-sm font-semibold text-raro-gray-900">
                    <input
                        type="checkbox"
                        checked={reimbursed}
                        disabled={reimbursed || reimburse.isPending}
                        onChange={() => void reimburse.mutateAsync(request.id).catch(() => undefined)}
                        className="h-5 w-5 rounded border-raro-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Reembolso realizado
                    {reimbursed && <CheckCircleIcon className="h-5 w-5 text-emerald-600" aria-hidden="true" />}
                </label>
            )}
        </div>
    );
}

export function HealthPlanReview({ request }: { request: HealthPlanRequest }) {
    const decide = useDecideHealthPlan();
    const meta = describeRequestStatus(request);
    const [effectiveDate, setEffectiveDate] = useState('');
    const needsDate = request.action === 'change' || (request.action === 'include' && request.includeHolder);

    return (
        <div className="space-y-6">
            <StatusPill label={meta.label} tone={meta.tone} />
            <HealthPlanSummary request={request} />

            {request.status === 'pending_dp' && (
                <div className="space-y-4">
                    <FormInput
                        label={needsDate ? 'Data em que o plano passa a valer' : 'Data de vigência (opcional)'}
                        name="effectiveDate"
                        type="date"
                        value={effectiveDate}
                        onChange={event => setEffectiveDate(event.target.value)}
                        helperText="Ao concluir, o cadastro de saúde do colaborador é atualizado."
                        required={needsDate}
                    />
                    <DecisionActions
                        busy={decide.isPending}
                        primaryLabel="Concluir e atualizar cadastro"
                        primaryDisabled={needsDate && effectiveDate === ''}
                        helper="Se o cadastro de saúde mudou depois do envio, o sistema não aplica e pede para recusar."
                        onPrimary={() => decide.mutateAsync({ requestId: request.id, action: 'approve', effectiveDate: effectiveDate || undefined })}
                        onReject={note => decide.mutateAsync({ requestId: request.id, action: 'reject', note })}
                    />
                </div>
            )}
        </div>
    );
}
